import { type ScriptableTooltipContext, type TooltipModel } from "chart.js";
import moment from "moment";

import { type TileFilterQuery } from "@features/metrics-explorer/types";
import {
  type ITimeGranularityV2,
  type DateInterval,
  type INBAccountingMode,
} from "@north-beam/nb-common";

interface BodyLine {
  datasetIndex: number;
  label: string;
  attributionModel: string;
  attributionWindow: string;
  accountingMode: string;
  accountingModeRaw: INBAccountingMode;
  timeGranularity: ITimeGranularityV2;
  dateRange: DateInterval;
  filterQuery: TileFilterQuery | null;
  value: string;
}

export function chartTooltip(context: ScriptableTooltipContext<"line">) {
  const chart = context.chart;
  const tooltip = context.tooltip;

  let tooltipEl = document.getElementById("chartjs-metrics-explorer-tooltip");
  if (!tooltipEl) {
    tooltipEl = document.createElement("div");
    tooltipEl.id = "chartjs-metrics-explorer-tooltip";
    tooltipEl.style.position = "absolute";
    tooltipEl.style.pointerEvents = "none";
    tooltipEl.style.zIndex = "3000";
    document.body.appendChild(tooltipEl);
  }

  if (tooltip.opacity === 0) {
    tooltipEl.style.opacity = "0";
    tooltipEl.style.transition = "opacity 0.1s ease-out";
    return;
  }

  if (tooltip.body) {
    const lines = tooltip.body.flatMap((b) => b.lines) as unknown as BodyLine[];

    const innerHtml = tooltipContent(tooltip, lines);
    tooltipEl.innerHTML = innerHtml;
  }

  const position = chart.canvas.getBoundingClientRect();
  const tooltipElRect = tooltipEl.getBoundingClientRect();

  // Position tooltip to the right of the cursor by default
  let left = position.left + window.scrollX + tooltip.caretX + 10;

  // If tooltip would go off screen to the right, position it to the left of the cursor
  if (left + tooltipElRect.width > window.innerWidth) {
    left =
      position.left +
      window.scrollX +
      tooltip.caretX -
      tooltipElRect.width -
      10;
  }

  // Ensure tooltip stays within viewport bounds
  if (left < 0) {
    left = 5;
  }

  // Center tooltip vertically relative to cursor
  const top =
    position.top + window.scrollY + tooltip.caretY - tooltipElRect.height / 2;

  tooltipEl.style.opacity = "1";
  tooltipEl.style.left = `${left}px`;
  tooltipEl.style.top = `${top}px`;
  tooltipEl.style.transition = "opacity 0.1s ease-in";
}

function tooltipContent(
  tooltip: TooltipModel<"line">,
  lines: BodyLine[],
): string {
  const tableHeaderHtml = `
    <div class="flex text-xs border-b border-v2-tertiary bg-v2-primary">
      <div class="flex-1 px-4 py-2 whitespace-nowrap border-r border-v2-tertiary font-medium text-v2-primary min-w-[200px]">
        Metric
      </div>
      <div class="px-4 py-2 whitespace-nowrap text-right font-medium text-v2-primary border-r border-v2-tertiary w-24 shrink-0">
        Value
      </div>
      <div class="px-4 py-2 whitespace-nowrap text-right font-medium text-v2-primary w-40 shrink-0">
        Date
      </div>
    </div>
  `;

  const bodyRowsHtml = lines
    .map((line, index) => {
      const labelStyle = tooltip.labelColors[index];
      const rowDateStr = moment(line.dateRange.startDate).format(
        line.timeGranularity === "hourly" ? "MMM DD, YYYY, hA" : "MMM DD, YYYY",
      );

      const legendCircle = `
        <div
          class="w-2.5 h-2.5 rounded-full shrink-0"
          style="background-color: ${
            labelStyle.borderColor || labelStyle.backgroundColor
          }"
        ></div>
      `;

      const bottomBorderClass =
        index === lines.length - 1 ? "" : "border-b border-v2-tertiary";

      return `
        <div class="flex text-xs ${bottomBorderClass}">
          <div class="flex-1 px-4 py-1.5 whitespace-nowrap bg-v2-primary border-r border-v2-tertiary min-w-[200px]">
            <div class="flex items-center gap-2">
              ${legendCircle}
              <div>
                <span class="text-v2-primary font-medium truncate">${line.label}</span>
                <div class="text-v2-secondary text-[11px]">
                  ${line.attributionWindow} &bull; ${line.attributionModel} &bull; ${line.accountingMode}
                </div>
              </div>
            </div>
          </div>
          <div class="flex flex-row-reverse px-4 py-2 whitespace-nowrap bg-v2-primary items-center font-mono text-v2-primary border-r border-v2-tertiary w-24 shrink-0">
            ${line.value}
          </div>
          <div class="flex flex-row-reverse px-4 py-2 whitespace-nowrap bg-v2-primary items-center font-mono text-v2-primary w-40 shrink-0">
            ${rowDateStr}
          </div>
        </div>
      `;
    })
    .join("");

  const fullContentHtml = `
    <div class="bg-v2-glass-inverse backdrop-blur-sm rounded-[8px] shadow-lg text-xs p-1">
      <div class="bg-v2-primary rounded-[6px] overflow-hidden border border-v2-tertiary">
        <div>
          ${tableHeaderHtml}
          ${bodyRowsHtml}
        </div>
      </div>
    </div>
  `;

  return fullContentHtml;
}
